import React from "react";
import Avatar, { AvatarProps } from "@mui/material/Avatar";
import { useResolvedMedia } from "../../hooks/useResolvedMedia";

interface ResolvedAvatarProps extends Omit<AvatarProps, "src"> {
  /** May be a plain image URL, a data/blob URL, or a compressor manifest (`.json`). */
  src?: string;
}

/**
 * ResolvedAvatar wraps MUI `Avatar` and resolves a `src` that may be a
 * compressor manifest (a `.json`) into a real variant URL before binding it.
 *
 * When `src` is empty (or resolves to nothing) MUI's Avatar shows its built in
 * person icon, so there is no external placeholder image to reach for. A raw
 * manifest never renders as a broken image because the manifest is resolved
 * first. A plain URL passes straight through.
 */
export const ResolvedAvatar: React.FC<ResolvedAvatarProps> = ({ src, className, ...rest }) => {
  const resolved = useResolvedMedia(src ?? "");
  return (
    <Avatar
      src={resolved || undefined}
      className={["resolvedAvatar", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
};

interface ResolvedOrgAvatarImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** May be a plain image URL or a compressor manifest (`.json`). */
  src?: string;
  /** Rendered when there is no image to show (empty src or still resolving). */
  fallback: React.ReactNode;
}

/**
 * ResolvedOrgAvatarImg renders a plain `<img>` whose `src` may be a manifest,
 * resolving it first. When there is nothing to show it renders `fallback`
 * instead of a broken image. Used by the negotiate coin avatars, where the
 * empty state is a `GroupsRounded` glyph rather than a person icon.
 * @story args { src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect width='96' height='96' rx='24' fill='%23f59e0b'/%3E%3C/svg%3E", fallback: "Organizacao" }
 */
export const ResolvedOrgAvatarImg: React.FC<ResolvedOrgAvatarImgProps> = ({
  src,
  fallback,
  alt = "",
  className,
  ...rest
}) => {
  const resolved = useResolvedMedia(src ?? "");
  if (!resolved) return <>{fallback}</>;
  return (
    <img
      src={resolved}
      alt={alt}
      className={["resolvedOrgAvatarImg", className].filter(Boolean).join(" ")}
      {...rest}
    />
  );
};

export default ResolvedAvatar;
